import { createSelector } from '@reduxjs/toolkit';
import { AssetSchemaRootState } from '../store';
import { fromRawSystem, SystemType } from '@innovyze/lib_asset_schema';
import { AssetSchemaStoreState } from '../types';
import { assetSchemaBaseSelector } from './shared';

/**
 * Selector to get the system schema from the asset schema store.
 * @param schemas - The asset schema store state.
 * @returns An object mapping system types to their corresponding system schema.
 * @throws Will throw an error if the asset schema store is missing.
 */
export const systemSchemaSelector = createSelector(
	[assetSchemaBaseSelector],
	(schemas: AssetSchemaStoreState) => {
		if (schemas == undefined) {
			throw new Error(
				'Asset-Schema store is missing! Did you setup the @innovyze/lib_asset_schema_store assetSchemaRootReducer and assetSchemaRootSaga correctly?',
			);
		}

		const result: { [systemType: string]: SystemType } = {};

		for (const rawSystem of Object.values(schemas.systemTypes)) {
			result[rawSystem.SystemType] = fromRawSystem(rawSystem);
		}

		return result;
	},
);

/**
 * Selector to get a specific system schema by its type from the asset schema store.
 * @param schemas - The asset schema store state.
 * @param systemType - The type of the system to retrieve.
 * @returns The system schema for the specified type, or undefined if not found.
 * @throws Will throw an error if the asset schema store is missing.
 */
export const systemSchemaSelectorByType = createSelector(
	[
		assetSchemaBaseSelector,
		(_state: AssetSchemaRootState, systemType: string) => systemType,
	],
	(schemas: AssetSchemaStoreState, systemType: string) => {
		if (schemas == undefined) {
			throw new Error(
				'Asset-Schema store is missing! Did you setup the @innovyze/lib_asset_schema_store assetSchemaRootReducer and assetSchemaRootSaga correctly?',
			);
		}

		const rawSystem = schemas.systemTypes[systemType];

		if (!rawSystem) {
			return undefined;
		}

		return fromRawSystem(rawSystem);
	},
);
